import { useEffect, useMemo, useRef, useState } from "react";
import { TransformWrapper, TransformComponent } from "react-zoom-pan-pinch";
import {
  ArrowsClockwise,
  CaretDown,
  Table as TableIcon,
} from "@phosphor-icons/react";
import {
  PROYECTOS,
  type LoteSVG,
  type ProyectoCode,
} from "../../types/sitePlan";
import { useAppStore } from "@/store/appStore";
import {
  useSitePlanLotes,
  applySitePlanFilters,
} from "@/hooks/useSitePlan";
import { LoteInfoPanel } from "./LoteInfoPanel";
import { LoteTable } from "./LoteTable";
import { ResizableDrawer } from "./ResizableDrawer";

const HOVER_STROKE = "#fb4195";
const SELECTED_STROKE = "#000000";

export function SitePlan() {
  const proyecto = useAppStore((s) => s.sitePlanProject);
  const setProyecto = useAppStore((s) => s.setSitePlanProject);
  const filters = useAppStore((s) => s.sitePlanFilters);
  const search = useAppStore((s) => s.search);
  const tableOpen = useAppStore((s) => s.sitePlanTableOpen);
  const setTableOpen = useAppStore((s) => s.setSitePlanTableOpen);

  const { data: lotes = [] as LoteSVG[], isLoading, error } =
    useSitePlanLotes(proyecto);

  const [selected, setSelected] = useState<LoteSVG | null>(null);
  const [hoverId, setHoverId] = useState<number | null>(null);
  const [projectMenuOpen, setProjectMenuOpen] = useState(false);
  const bgRef = useRef<SVGGElement | null>(null);

  // Limpiar selección al cambiar de proyecto
  useEffect(() => {
    setSelected(null);
  }, [proyecto]);

  // Cargar SVG de fondo cuando cambia el proyecto
  useEffect(() => {
    const ctrl = new AbortController();
    const url = PROYECTOS[proyecto].bgImage;
    fetch(url, { signal: ctrl.signal })
      .then((r) => r.text())
      .then((text) => {
        if (!bgRef.current) return;
        const m = text.match(/<svg[^>]*>([\s\S]*?)<\/svg>/);
        bgRef.current.innerHTML = m ? m[1] : "";
      })
      .catch((err: unknown) => {
        if (err instanceof Error && err.name !== "AbortError") {
          if (bgRef.current) bgRef.current.innerHTML = "";
        }
      });
    return () => ctrl.abort();
  }, [proyecto]);

  // Lotes filtrados
  const lotesFiltrados = useMemo(
    () => applySitePlanFilters(lotes, filters, search),
    [lotes, filters, search],
  );

  const visibleIds = useMemo(
    () => new Set(lotesFiltrados.map((l: LoteSVG) => l.idLote)),
    [lotesFiltrados],
  );

  const config = PROYECTOS[proyecto];

  // ViewBox dinámico ajustado al bbox real de los lotes (con 10% de padding).
  // Si el proyecto pide viewBox estático (useStaticViewBox), usamos el del SVG
  // completo — los lotes no cubren todo el conjunto y el auto-ajuste recortaba.
  const contentViewBox = useMemo(() => {
    if (config.useStaticViewBox) return config.viewBox;
    if (lotes.length === 0) return config.viewBox;
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    const re = /[ML]\s+(-?\d+\.?\d*)\s+(-?\d+\.?\d*)/g;
    for (const l of lotes) {
      let m: RegExpExecArray | null;
      re.lastIndex = 0;
      while ((m = re.exec(l.dPath)) !== null) {
        const x = parseFloat(m[1]);
        const y = parseFloat(m[2]);
        if (x < minX) minX = x;
        if (x > maxX) maxX = x;
        if (y < minY) minY = y;
        if (y > maxY) maxY = y;
      }
    }
    if (!isFinite(minX)) return config.viewBox;
    const w = maxX - minX;
    const h = maxY - minY;
    const padX = w * 0.1;
    const padY = h * 0.1;
    return `${minX - padX} ${minY - padY} ${w + padX * 2} ${h + padY * 2}`;
  }, [lotes, config.viewBox, config.useStaticViewBox]);

  return (
    <div
      className="relative h-full w-full overflow-hidden"
      style={{ backgroundColor: "#F3F3F3", fontFamily: "Roboto, sans-serif" }}
    >
      {/* Pill unificado: contador "X Lotes" (izq, verde lima) + selector de
          proyecto (der, oscuro). Footguns aprendidos en una app paralela:
          - el div con `filter: drop-shadow(...)` envuelve SOLO el pill, no
            el dropdown — sino el menu hereda la sombra del filter.
          - el dropdown vive como hermano del pill, dentro del wrapper
            `relative` externo, asi no se hereda el filter pero sigue
            anclado a `top-full` del wrapper.
          - rounded por lado (l-* en el contador, r-* en el button) en vez
            de un container con `overflow-hidden`, que clipearia el menu. */}
      <div className="absolute left-1/2 top-4 z-30 -translate-x-1/2">
        <div className="relative inline-flex">
          <div
            className="flex"
            style={{ filter: "drop-shadow(0 6px 0 rgba(0,0,0,0.16))" }}
          >
            <span
              className="flex items-center px-6 py-3 font-bold text-black"
              style={{
                backgroundColor: "#ADD010",
                borderTopLeftRadius: 32,
                borderBottomLeftRadius: 32,
                fontFamily: "'Roboto Mono', monospace",
                fontSize: 18,
              }}
            >
              {lotesFiltrados.length} Lotes
            </span>
            <button
              onClick={() => setProjectMenuOpen((o) => !o)}
              className="flex items-center gap-3 px-12 py-3 text-white font-semibold transition-colors hover:bg-[#163522]"
              style={{
                backgroundColor: "#0F2818",
                borderTopRightRadius: 32,
                borderBottomRightRadius: 32,
                fontSize: 18,
              }}
            >
              {config.nombre}
              <CaretDown size={14} weight="bold" />
            </button>
          </div>

          {projectMenuOpen && (
            <>
              <div
                className="fixed inset-0 z-30"
                onClick={() => setProjectMenuOpen(false)}
              />
              <div
                className="absolute left-1/2 top-full mt-2 -translate-x-1/2 z-40 bg-white py-2 min-w-[200px]"
                style={{
                  borderRadius: 16,
                  boxShadow: "0 6px 24px rgba(0,0,0,0.16)",
                }}
              >
                {(["VN", "VI", "VB", "VC"] as ProyectoCode[]).map((code: ProyectoCode) => (
                  <button
                    key={code}
                    onClick={() => {
                      setProyecto(code);
                      setProjectMenuOpen(false);
                    }}
                    className={`w-full text-left px-4 py-2 text-sm transition-colors ${
                      code === proyecto
                        ? "bg-stone-100 font-medium"
                        : "hover:bg-stone-50"
                    }`}
                    style={{ color: "#5D636C" }}
                  >
                    {PROYECTOS[code].nombre}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Mapa con pan/zoom */}
      <TransformWrapper
        initialScale={1}
        minScale={0.3}
        maxScale={6}
        wheel={{ step: 0.1 }}
        doubleClick={{ disabled: true }}
        panning={{ velocityDisabled: true }}
        centerOnInit
      >
        {({ resetTransform }) => (
          <>
            <TransformComponent
              wrapperStyle={{ width: "100%", height: "100%" }}
              contentStyle={{ width: "100%", height: "100%" }}
            >
              <div className="relative h-full w-full">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox={contentViewBox}
                  className="h-full w-full"
                  preserveAspectRatio="xMidYMid meet"
                  style={{ display: "block" }}
                >
                  <g ref={bgRef} data-bg />

{/* Pase 1: lotes normales (no seleccionado, no hovered) */}
{lotes.map((l: LoteSVG) => {
  if (selected?.idLote === l.idLote) return null;
  if (hoverId === l.idLote) return null;
  const visible = visibleIds.has(l.idLote);
  return (
    <path
      key={l.idLote}
      d={l.dPath}
      fill={l.colorHex}
      fillOpacity={
        !visible
          ? 0.15
          : selected
            ? 0.25
            : 1
      }
      stroke="white"
      strokeWidth={2}
      style={{
        cursor: visible ? "pointer" : "default",
        transition: "stroke 80ms, stroke-width 80ms",
      }}
      onClick={() => visible && setSelected(l)}
      onMouseEnter={() => visible && setHoverId(l.idLote)}
      onMouseLeave={() => setHoverId(null)}
    >
      <title>
        {l.lote}
        {l.cliente ? ` — ${l.cliente}` : ""}
      </title>
    </path>
  );
})}

{/* Pase 2: hover (encima de los normales, debajo del seleccionado) */}
{hoverId !== null && hoverId !== selected?.idLote && (() => {
  const l = lotes.find((x) => x.idLote === hoverId);
  if (!l) return null;
  const visible = visibleIds.has(l.idLote);
  if (!visible) return null;
  return (
    <path
      key={`hover-${l.idLote}`}
      d={l.dPath}
      fill={l.colorHex}
      fillOpacity={selected ? 0.25 : 1}
      stroke={HOVER_STROKE}
      strokeWidth={3}
      style={{
        cursor: "pointer",
        transition: "stroke 80ms, stroke-width 80ms",
      }}
      onClick={() => setSelected(l)}
      onMouseLeave={() => setHoverId(null)}
    >
      <title>
        {l.lote}
        {l.cliente ? ` — ${l.cliente}` : ""}
      </title>
    </path>
  );
})()}

{/* Pase 3: seleccionado (siempre encima de todo) */}
{selected && (() => {
  const l = lotes.find((x) => x.idLote === selected.idLote);
  if (!l) return null;
  return (
    <path
      key={`selected-${l.idLote}`}
      d={l.dPath}
      fill={l.colorHex}
      fillOpacity={1}
      stroke={SELECTED_STROKE}
      strokeWidth={5}
      style={{
        cursor: "pointer",
        transition: "stroke 80ms, stroke-width 80ms",
      }}
      onMouseEnter={() => setHoverId(l.idLote)}
      onMouseLeave={() => setHoverId(null)}
    >
      <title>
        {l.lote}
        {l.cliente ? ` — ${l.cliente}` : ""}
      </title>
    </path>
  );
})()}
                </svg>
              </div>
            </TransformComponent>

            {/* Botón: Reset zoom */}
            <button
              onClick={() => resetTransform()}
              className="absolute right-6 top-20 z-20 flex h-10 w-10 items-center justify-center bg-white transition-shadow hover:shadow-md"
              style={{
                borderRadius: 8,
                border: "1px solid #D9D9D9",
                color: "#5D636C",
              }}
              title="Restablecer zoom"
            >
              <ArrowsClockwise size={18} weight="bold" />
            </button>

            {/* Botón: Abrir tabla */}
            <button
              onClick={() => setTableOpen(true)}
              className="absolute right-6 top-32 z-20 flex h-10 w-10 items-center justify-center bg-white transition-shadow hover:shadow-md"
              style={{
                borderRadius: 8,
                border: "1px solid #D9D9D9",
                color: "#5D636C",
              }}
              title="Ver tabla de lotes"
            >
              <TableIcon size={18} weight="bold" />
            </button>
          </>
        )}
      </TransformWrapper>

      {isLoading && (
        <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/40 backdrop-blur-sm">
          <div className="text-sm font-semibold" style={{ color: "#5D636C" }}>
            Cargando lotes…
          </div>
        </div>
      )}
      {error && (
        <div
          className="absolute left-1/2 top-20 z-30 -translate-x-1/2 px-4 py-2 text-sm text-white"
          style={{ backgroundColor: "#BB4A4A", borderRadius: 8 }}
        >
          {error instanceof Error ? error.message : "Error cargando lotes"}
        </div>
      )}

      {/* Card de info del lote (siempre que haya selección) */}
      {selected && (
        <LoteInfoPanel lote={selected} onClose={() => setSelected(null)} />
      )}

      {/* Drawer con tabla */}
      <ResizableDrawer
        open={tableOpen}
        onClose={() => setTableOpen(false)}
      >
        <LoteTable
          lotes={lotesFiltrados}
          selectedId={selected?.idLote ?? null}
          onRowClick={(lote) => setSelected(lote)}
          onClose={() => setTableOpen(false)}
        />
      </ResizableDrawer>
    </div>
  );
}